import React, { useEffect, useState } from 'react';
import { EmailMapping, EmailSender, EmailTemplate, getMappings, getSenders, getTemplates, createMapping, deleteMapping } from '../../services/emailApi';

const EmailMappingsPage: React.FC = () => {
  const [mappings, setMappings] = useState<EmailMapping[]>([]);
  const [senders, setSenders] = useState<EmailSender[]>([]);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedSenderId, setSelectedSenderId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [m, s, t] = await Promise.all([getMappings(), getSenders(), getTemplates()]);
  setMappings(Array.isArray(m) ? m : (m?.mappings || []));
  setSenders(Array.isArray(s) ? s : (s?.senders || []));
  setTemplates(Array.isArray(t) ? t : (t?.templates || []));
    } catch (e: any) {
      setError(e?.message || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const addMapping = async () => {
    setError(null);
    setInfo(null);
    if (!selectedSenderId || !selectedTemplateId) return;
    // Check for duplicate mapping
    const exists = mappings.some(
      m => String(m.emailSenderId) === String(selectedSenderId) && String(m.emailTemplateId) === String(selectedTemplateId)
    );
    if (exists) {
      setError('A mapping for this sender and template already exists.');
      return;
    }
    try {
      const res = await createMapping({ emailSenderId: selectedSenderId, emailTemplateId: selectedTemplateId });
      if (res && res.message) {
        setInfo(res.message);
      }
      setSelectedSenderId('');
      setSelectedTemplateId('');
      await load();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setError('A mapping for this sender and template already exists.');
      } else if (e?.response?.data?.message) {
        setError(e.response.data.message);
      } else {
        setError(e?.message || 'Failed to create mapping');
      }
    }
  };

  const removeMapping = async (id?: string) => {
    if (!id) return;
    await deleteMapping(id);
    await load();
  };

  return (
    <div className="p-6 bg-background min-h-screen">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-primary">Email Mappings</h1>
      </div>

      <div className="bg-white rounded shadow p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <label className="text-sm text-secondary">Sender</label>
          <select
            value={selectedSenderId}
            onChange={(e) => setSelectedSenderId(e.target.value)}
            className="border rounded px-3 py-2 text-sm w-full"
          >
            <option value="">Select sender</option>
            {senders.filter(s => s.id).map(s => (
              <option key={s.id} value={s.id}>{s.emailAddress}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm text-secondary">Template</label>
          <select
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value)}
            className="border rounded px-3 py-2 text-sm w-full"
          >
            <option value="">Select template</option>
            {templates.map(t => (
              <option key={t.id || t.name} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <button onClick={addMapping} className="px-4 py-2 bg-primary text-white rounded">Add Mapping</button>
        </div>
      </div>

  {error && <div className="mb-3 text-red-600 text-sm">{error}</div>}
  {info && <div className="mb-3 text-green-700 text-sm">{info}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="bg-white rounded shadow overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-left p-2">ID</th>
                <th className="text-left p-2">Sender</th>
                <th className="text-left p-2">Template</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {mappings.map(m => {
                // Always compare as strings for IDs
                const s = m.emailSender || senders.find(x => String(x.id) === String(m.emailSenderId));
                const t = m.emailTemplate || templates.find(x => String(x.id) === String(m.emailTemplateId));
                // Some API responses may use 'templateName' instead of 'name'
                const templateDisplayName = t?.name || (t && (t as any).templateName) || m.emailTemplateId;
                return (
                  <tr key={m.id || `${m.emailSenderId}-${m.emailTemplateId}`} className="border-t">
                    <td className="p-2">{m.id || '-'}</td>
                    <td className="p-2">{s?.emailAddress || m.emailSenderId}</td>
                    <td className="p-2">{templateDisplayName}</td>
                    <td className="p-2">
                      {m.id && (
                        <button onClick={() => removeMapping(m.id)} className="text-red-600 hover:underline">Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {mappings.length === 0 && (
                <tr>
                  <td className="p-4" colSpan={4}>No mappings</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EmailMappingsPage;
